import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const TOMORROW_API_KEY = process.env.REACT_APP_TOMORROW_API_KEY;
const ATTENDANCE_QR_PAGE_URL = 'https://steklolux.kz/qr-attendance#YrSavQ_cwAkDRuwPg7i4RhE0OK3TtquUvIMikrWmhPM';
const TV_DASHBOARD_FRAME_STYLE = {
  width: '1360px',
  height: '768px',
  maxWidth: '1360px',
  maxHeight: '768px',
  minWidth: '1360px',
  minHeight: '768px',
};
const ALMATY_SCHEDULE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Almaty',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const shouldShowScheduledQr = (date) => {
  const parts = Object.fromEntries(
    ALMATY_SCHEDULE_FORMATTER
      .formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );
  const hour = Number(parts.hour);
  const minute = Number(parts.minute);
  const minutesSinceMidnight = hour * 60 + minute;

  if (parts.weekday === 'Sun') return true;

  const dashboardStartMinutes = 9 * 60 + 15;
  const dashboardEndMinutes = (parts.weekday === 'Sat' ? 14 : 18) * 60;
  return minutesSinceMidnight < dashboardStartMinutes || minutesSinceMidnight >= dashboardEndMinutes;
};

const TVDashboardPage = () => {
  // Функция для проверки, является ли день рабочим (понедельник-пятница)
  const isWorkingDay = (date) => {
    const day = date.getDay();
    return day >= 1 && day <= 5;
  };

  // Функция для получения следующего рабочего дня
  const getNextWorkingDay = (date) => {
    let nextDay = new Date(date);
    do {
      nextDay.setDate(nextDay.getDate() + 1);
    } while (!isWorkingDay(nextDay));
    return nextDay;
  };

  // Функция для определения стартовой даты заказа с учетом времени поступления
  const getOrderStartDate = (createdAt) => {
    const orderDate = new Date(createdAt);
    const orderHour = orderDate.getHours();
    
    // Если заказ поступил после 12:00, считаем что он начнется завтра
    if (orderHour >= 12) {
      return getNextWorkingDay(orderDate);
    }
    
    // Если поступил до 12:00 в рабочий день, начинается сегодня
    if (isWorkingDay(orderDate)) {
      return orderDate;
    }
    
    // Если поступил в выходной, начнется в следующий рабочий день
    return getNextWorkingDay(orderDate);
  };

  // Функция для расчета рабочих дней от старта заказа до сегодня
  const getWorkingDaysFromOrderStart = (createdAt) => {
    const orderDate = new Date(createdAt);
    const orderHour = orderDate.getHours();
    const today = new Date();
    
    // Если заказ поступил сегодня после 12:00, возвращаем 0 (нулевой день)
    if (orderDate.toDateString() === today.toDateString() && orderHour >= 12) {
      return 0;
    }
    
    // Если заказ поступил сегодня до 12:00, считаем как первый день
    if (orderDate.toDateString() === today.toDateString() && orderHour < 12) {
      return 1;
    }
    
    const startDate = getOrderStartDate(createdAt);
    let current = new Date(startDate);
    let workingDays = 0;
    
    // Если стартовая дата еще не наступила
    if (startDate > today) {
      return 0;
    }
    
    while (current < today) {
      if (isWorkingDay(current)) {
        workingDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    // Если сегодня рабочий день и заказ уже начался, добавляем сегодняшний день
    if (isWorkingDay(today) && startDate <= today) {
      workingDays++;
    }
    
    return workingDays;
  };

  // Функция для определения цвета заказа по срокам с учетом стадии
  const getOrderColorByDeadline = (workingDaysFromStart, stageName) => {
    const stageKey = stageName ? stageName.toLowerCase() : '';
    
    // Серые стадии - всегда серый цвет (только финальные стадии)
    const greyStages = ['самовывоз', 'комплектация', 'готово склад', 'отправка'];
    if (greyStages.some(stage => stageKey.includes(stage))) {
      return '#6B7280'; // Серый
    }
    
    // Обычные стадии с цветовой индикацией по срокам
    if (workingDaysFromStart <= 3) {
      return '#10B981'; // Зеленый (первые 3 дня)
    } else if (workingDaysFromStart <= 6) {
      return '#F59E0B'; // Желтый (следующие 3 дня)
    } else {
      return '#EF4444'; // Красный (остальные дни)
    }
  };

  // Функция для форматирования номера заказа с учетом переделки
  const formatOrderNumber = (orderNumber, title, stageName) => {
    const isRework = title && (
      title.toLowerCase().includes('переделка') || 
      title.toLowerCase().includes('бой') ||
      (stageName && stageName.toLowerCase().includes('закалка') && title.toLowerCase().includes('бой'))
    );
    return isRework ? `${orderNumber}П` : orderNumber;
  };

  // Добавляем CSS стили для анимации прокрутки
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes scrollOrders {
        0% {
          transform: translateY(0);
        }
        25% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-40%);
        }
        75% {
          transform: translateY(-40%);
        }
        100% {
          transform: translateY(0);
        }
      }
      
      .animate-scroll {
        animation: scrollOrders 15s ease-in-out infinite;
        animation-play-state: running;
      }
      
      .animate-scroll:hover {
        animation-play-state: paused;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [dashboardData, setDashboardData] = useState(null);
  const [potentialOrdersCount, setPotentialOrdersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showScheduledQr, setShowScheduledQr] = useState(() => shouldShowScheduledQr(new Date()));
  const [weather, setWeather] = useState(null);

  // Refs для предотвращения лишних перерисовок
  const prevDashboardData = useRef(null);
  const prevPotentialCount = useRef(0);
  const prevWeather = useRef(null);
  const prevCuttingOrdersCount = useRef(0);

  // Функция воспроизведения звукового сигнала
  const playNotificationSound = useCallback(() => {
    try {
      // Сначала пробуем воспроизвести готовый звуковой файл
      const audio = new Audio('/sounds/new-order.mp3');
      audio.volume = 0.7;
      
      audio.play().then(() => {
        console.log('🔊 Воспроизведен звуковой файл: новый заказ на резку!');
      }).catch(() => {
        // Если файл не найден, используем синтезированный звук
        console.log('Звуковой файл не найден, используем синтезированную мелодию');
        playGeneratedSound();
      });
      
    } catch (error) {
      console.error('Ошибка воспроизведения звука:', error);
      // В случае ошибки тоже пробуем синтезированный звук
      playGeneratedSound();
    }
  }, []);

  // Функция для воспроизведения синтезированного звука
  const playGeneratedSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Вариант C: Более длинная мелодия (активен)
      const notes = [
        { freq: 440.00, time: 0.0, duration: 0.15 }, // Ля
        { freq: 523.25, time: 0.15, duration: 0.15 }, // До
        { freq: 659.25, time: 0.3, duration: 0.15 }, // Ми
        { freq: 783.99, time: 0.45, duration: 0.15 }, // Соль
        { freq: 880.00, time: 0.6, duration: 0.3 } // Ля (октава)
      ];
      
      notes.forEach(note => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(note.freq, audioContext.currentTime + note.time);
        oscillator.type = 'sine'; // Можно попробовать 'square', 'sawtooth', 'triangle'
        
        // Настройка громкости с плавным затуханием
        gainNode.gain.setValueAtTime(0, audioContext.currentTime + note.time);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + note.time + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + note.time + note.duration);
        
        oscillator.start(audioContext.currentTime + note.time);
        oscillator.stop(audioContext.currentTime + note.time + note.duration);
      });
      
      console.log('🔊 Новый заказ на резку!');
    } catch (error) {
      console.error('Ошибка воспроизведения звука:', error);
    }
  }, []);

  // Получение данных панели заказов (без полного обновления страницы)
  const fetchDashboardData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('TV mode: No token found, attempting guest access');
      }
      
      const response = await axios.get(`${API_URL}/api/orders-dashboard`, {
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      if (response.data.success) {
        const newData = response.data.data;
        const newDataStr = JSON.stringify(newData);
        const prevDataStr = JSON.stringify(prevDashboardData.current);
        
        if (newDataStr !== prevDataStr) {
          console.log('Dashboard data updated at:', new Date().toLocaleTimeString());
          
          // Проверяем изменения в количестве заказов на резке
          if (newData.stages) {
            const cuttingStage = newData.stages.find(stage => 
              stage.name === 'Резка' || stage.stage === 'cutting'
            );
            
            if (cuttingStage) {
              const newCuttingCount = cuttingStage.count || 0;
              const prevCuttingCount = prevCuttingOrdersCount.current;
              
              // Воспроизводим звук только если это не первая загрузка и количество увеличилось
              if (!isInitialLoad && newCuttingCount > prevCuttingCount && prevCuttingCount > 0) {
                console.log(`Новый заказ на резку! Было: ${prevCuttingCount}, стало: ${newCuttingCount}`);
                playNotificationSound();
              }
              
              // Обновляем счетчик в любом случае
              prevCuttingOrdersCount.current = newCuttingCount;
            }
          }
          
          prevDashboardData.current = newData;
          setDashboardData(newData);
        }
      } else {
        throw new Error(response.data.error || 'Ошибка получения данных');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      if (isInitialLoad) {
        setError(err.response?.data?.error || err.message || 'Ошибка загрузки данных');
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Получение только счетчика потенциальных заказов (без полного обновления)
  const fetchPotentialOrdersCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('TV mode: No token for CRM data');
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/crm/deals`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          'stageId': 'C25:UC_O0MXOK',
          'limit': 1 // Получаем только один элемент для подсчета
        }
      });

      let newCount = 0;
      if (response.data && response.data.success && response.data.data && response.data.data.deals) {
        newCount = response.data.data.deals.length;
      } else if (response.data && response.data.result) {
        newCount = response.data.result.length;
      }
      
      if (newCount !== prevPotentialCount.current) {
        console.log('Potential orders count updated at:', new Date().toLocaleTimeString(), 'Count:', newCount);
        prevPotentialCount.current = newCount;
        setPotentialOrdersCount(newCount);
      }
    } catch (err) {
      console.error('Error fetching potential orders count:', err);
      // Устанавливаем тестовое значение только если его еще нет
      if (prevPotentialCount.current === 0) {
        const testCount = 3;
        prevPotentialCount.current = testCount;
        setPotentialOrdersCount(testCount);
      }
    }
  }, []);


  // Получение погоды для Алматы через Tomorrow.io (без полного обновления)
  const fetchWeather = useCallback(async () => {
    try {
      if (!TOMORROW_API_KEY) {
        console.warn('REACT_APP_TOMORROW_API_KEY is not configured');
        return;
      }

      // Координаты Алматы: 43.2220, 76.8512
      const response = await axios.get('https://api.tomorrow.io/v4/weather/realtime', {
        params: {
          location: '43.2220,76.8512',
          apikey: TOMORROW_API_KEY
        }
      });

      if (response.data && response.data.data && response.data.data.values) {
        const values = response.data.data.values;
        const weatherData = {
          main: { temp: Math.round(values.temperature) },
          weather: [{
            description: getWeatherDescription(values.weatherCode || 0),
            icon: null
          }],
          location: { name: 'Алматы' }
        };
        
        const newWeatherStr = JSON.stringify(weatherData);
        const prevWeatherStr = JSON.stringify(prevWeather.current);
        
        if (newWeatherStr !== prevWeatherStr) {
          console.log('Weather updated at:', new Date().toLocaleTimeString());
          prevWeather.current = weatherData;
          setWeather(weatherData);
        }
      } else {
        throw new Error('Invalid weather data received');
      }
    } catch (err) {
      console.error('Error fetching weather:', err);
      // Устанавливаем тестовые данные только если их еще нет
      if (!prevWeather.current) {
        const testWeather = {
          main: { temp: -5 },
          weather: [{ description: 'Ясно', icon: null }],
          location: { name: 'Алматы' }
        };
        prevWeather.current = testWeather;
        setWeather(testWeather);
      }
    }
  }, []);

  // Функция для преобразования кода погоды Tomorrow.io в описание
  const getWeatherDescription = (weatherCode) => {
    const weatherCodes = {
      0: 'Неизвестно',
      1000: 'Ясно',
      1001: 'Облачно',
      1100: 'Переменная облачность',
      1101: 'Частично облачно',
      1102: 'Переменная облачность',
      2000: 'Туман',
      2100: 'Легкий туман',
      4000: 'Моросящий дождь',
      4001: 'Дождь',
      4200: 'Легкий дождь',
      4201: 'Сильный дождь',
      5000: 'Снег',
      5001: 'Снегопад',
      5100: 'Легкий снег',
      5101: 'Сильный снег',
      6000: 'Мокрый снег',
      6001: 'Мокрый снег',
      6200: 'Легкий мокрый снег',
      6201: 'Сильный мокрый снег',
      7000: 'Град',
      7101: 'Сильный град',
      7102: 'Легкий град',
      8000: 'Гроза'
    };
    return weatherCodes[weatherCode] || 'Неизвестно';
  };

  // Обновление времени в реальном времени
  useEffect(() => {
    const syncScheduleAndClock = () => {
      const now = new Date();
      setCurrentTime(now);
      setShowScheduledQr(shouldShowScheduledQr(now));
    };

    syncScheduleAndClock();
    const timeInterval = window.setInterval(syncScheduleAndClock, 1000);

    // Некоторые браузеры телевизоров замедляют таймеры после сна или потери фокуса.
    // При возвращении экрана сразу сверяем режим с текущим временем Алматы.
    window.addEventListener('focus', syncScheduleAndClock);
    window.addEventListener('pageshow', syncScheduleAndClock);
    document.addEventListener('visibilitychange', syncScheduleAndClock);

    return () => {
      window.clearInterval(timeInterval);
      window.removeEventListener('focus', syncScheduleAndClock);
      window.removeEventListener('pageshow', syncScheduleAndClock);
      document.removeEventListener('visibilitychange', syncScheduleAndClock);
    };
  }, []);


  // Автоматическое обновление данных дашборда (без медиа)
  useEffect(() => {
    // Первоначальная загрузка
    fetchDashboardData(true); // с флагом isInitialLoad
    fetchPotentialOrdersCount();
    fetchWeather();

    // Независимые интервалы для каждой секции
    const dashboardInterval = setInterval(() => {
      fetchDashboardData(false); // без полного обновления страницы
    }, 30000);

    const potentialOrdersInterval = setInterval(() => {
      fetchPotentialOrdersCount();
    }, 30000);

    const weatherInterval = setInterval(() => {
      fetchWeather();
    }, 600000); // каждые 10 минут

    return () => {
      clearInterval(dashboardInterval);
      clearInterval(potentialOrdersInterval);
      clearInterval(weatherInterval);
    };
  }, [fetchPotentialOrdersCount, fetchWeather]);

  // Компонент карточки стадии для TV (полная версия с развернутым списком)
  const TVStageCard = React.memo(({ stage }) => {
    const handleOrderClick = (orderNumber) => {
      navigator.clipboard.writeText(orderNumber).then(() => {
        console.log(`Номер заказа ${orderNumber} скопирован в буфер обмена`);
      }).catch(err => {
        console.error('Ошибка копирования:', err);
      });
    };

    return (
      <div className="bg-white rounded-lg shadow-md border-l-4 p-1" 
           style={{ borderLeftColor: stage.color }}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-2">
            <div 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: stage.color }}
            ></div>
            <div className="flex items-center space-x-2">
              <h3 className="text-xs font-semibold text-gray-800">{stage.name}</h3>
              <span className="text-xs font-bold" style={{ color: stage.color }}>
                ({stage.count})
              </span>
              {stage.lastUpdated && (
                <span className="text-xs text-gray-500">
                  {new Date(stage.lastUpdated).toLocaleTimeString('ru-RU', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        {stage.error && (
          <div className="mb-1 p-1 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-xs">Ошибка: {stage.error}</p>
          </div>
        )}

        {stage.orders.length > 0 && (
          <div className="max-h-24 overflow-hidden relative">
            <div className={`flex flex-wrap gap-1 ${stage.orders.length > 12 ? 'animate-scroll' : ''}`}>
              {stage.orders.map((order) => {
                // Всегда конвертируем календарные дни в рабочие дни
                let workingDaysFromStart;
                if (order.createdAt) {
                  // Если есть точная дата создания, используем расчет рабочих дней
                  workingDaysFromStart = getWorkingDaysFromOrderStart(order.createdAt);
                } else if (order.daysFromStart !== undefined) {
                  // Конвертируем календарные дни в рабочие дни
                  // Предполагаем что заказ создан N календарных дней назад
                  const estimatedCreatedDate = new Date();
                  estimatedCreatedDate.setDate(estimatedCreatedDate.getDate() - order.daysFromStart);
                  
                  // Считаем рабочие дни от предполагаемой даты создания
                  let current = new Date(estimatedCreatedDate);
                  let workingDaysCount = 0;
                  const today = new Date();
                  
                  while (current <= today) {
                    if (isWorkingDay(current)) {
                      workingDaysCount++;
                    }
                    current.setDate(current.getDate() + 1);
                  }
                  
                  // Корректировка для правила 12:00
                  if (order.daysFromStart === 0) {
                    const currentHour = new Date().getHours();
                    workingDaysFromStart = currentHour < 12 ? 1 : 0;
                  } else {
                    workingDaysFromStart = Math.max(0, workingDaysCount - 1);
                  }
                } else {
                  workingDaysFromStart = 0;
                }
                
                const orderColor = getOrderColorByDeadline(workingDaysFromStart, stage.name);
                const formattedOrderNumber = formatOrderNumber(order.orderNumber, order.title, stage.name);
                const isRework = order.title && (
                  order.title.toLowerCase().includes('переделка') || 
                  order.title.toLowerCase().includes('бой') ||
                  (stage.name && stage.name.toLowerCase().includes('закалка') && order.title.toLowerCase().includes('бой'))
                );
                
                return (
                  <span
                    key={order.id}
                    onClick={() => handleOrderClick(formattedOrderNumber)}
                    className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium hover:opacity-80 transition-all cursor-pointer whitespace-nowrap"
                    style={{ 
                      backgroundColor: orderColor,
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '10px',
                      border: isRework ? '2px solid #8B5CF6' : 'none'
                    }}
                    title={`${order.title} (ID: ${order.id}) - ${workingDaysFromStart} р.д. от старта${isRework ? ' (ПЕРЕДЕЛКА)' : ''} - Нажмите чтобы скопировать номер`}
                  >
                    №{formattedOrderNumber}
                    <span className="ml-1 text-xs opacity-80">
                      {workingDaysFromStart}д
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {stage.count === 0 && !stage.error && (
          <div className="text-gray-500 text-xs italic">
            Нет заказов на данной стадии
          </div>
        )}
      </div>
    );
  });


  // Компонент статистики (компактная версия для 1360x768)
  const CompactSummaryCard = React.memo(({ title, value, subtitle, color = '#6B7280' }) => (
    <div className="bg-white rounded-lg shadow-md p-1">
      <div className="text-center">
        <p className="text-sm font-bold text-gray-700">{title}</p>
        <p className="text-xl font-bold" style={{ color }}>
          {value}
        </p>
        {subtitle && (
          <p className="text-xs text-gray-500">{subtitle}</p>
        )}
      </div>
    </div>
  ));

  // Мемоизированные компоненты секций для предотвращения перерисовок
  const ProductionSection = useMemo(() => {
    if (!dashboardData || !dashboardData.stages) return null;
    
    return (
      <div className="flex flex-col h-full">
        <h2 className="text-xs font-semibold text-white mb-1 flex items-center">
          <span className="mr-1">🏭</span>
          Производство
        </h2>
        <div className="flex-1 overflow-y-auto space-y-1">
          {dashboardData.stages.map((stage) => (
            <TVStageCard key={stage.stage} stage={stage} />
          ))}
        </div>
      </div>
    );
  }, [dashboardData?.stages]);

  const WarehouseSection = useMemo(() => {
    if (!dashboardData || !dashboardData.warehouse || dashboardData.warehouse.length === 0) return null;

    return (
      <div className="flex flex-col h-full">
        <h2 className="text-xs font-semibold text-white mb-1 flex items-center">
          <span className="mr-1">📦</span>
          Склад
        </h2>
        <div className="flex-1 overflow-y-auto space-y-1">
          {dashboardData.warehouse.map((stage) => (
            <TVStageCard key={stage.stage} stage={stage} />
          ))}
        </div>
      </div>
    );
  }, [dashboardData?.warehouse]);


  if (showScheduledQr) {
    return (
      <div className="overflow-hidden bg-gray-900" style={TV_DASHBOARD_FRAME_STYLE}>
        <iframe
          src={ATTENDANCE_QR_PAGE_URL}
          title="QR-табель — отметка рабочего времени"
          className="h-full w-full border-0 bg-[#edf3f3]"
          loading="eager"
          referrerPolicy="no-referrer"
          scrolling="no"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-white mb-4"></div>
          <p className="text-xl">Загрузка панели заказов...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-red-900 border border-red-700 rounded-lg p-8 text-white max-w-lg">
          <div className="text-center">
            <div className="text-red-400 text-4xl mb-4">⚠️</div>
            <h3 className="text-xl font-medium mb-4">Ошибка загрузки панели</h3>
            <p className="mb-4">{error}</p>
            <button
              onClick={() => {
                fetchDashboardData(true);
                fetchPotentialOrdersCount();
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md transition-colors"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white text-xl">
          Нет данных для отображения
        </div>
      </div>
    );
  }


  return (
    <div className="bg-gray-900 p-1 overflow-hidden" style={TV_DASHBOARD_FRAME_STYLE}>
      {/* Заголовок компактный */}
      <div className="mb-1">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white"></h1>
          </div>
        </div>
      </div>

      {/* Общая статистика */}
      <div className="grid grid-cols-6 gap-1 mb-2" style={{ height: '60px' }}>
        <CompactSummaryCard
          title="В производстве"
          value={dashboardData.summary?.productionOrders || 0}
          subtitle="активные"
          color="#10B981"
        />
        <CompactSummaryCard
          title="На складе"
          value={(() => {
            // Считаем заказы только из определенных стадий, исключая "готово"
            let totalCount = 0;
            
            // Ищем стадии со склада (warehouse), исключая готовые
            if (dashboardData.warehouse) {
              dashboardData.warehouse.forEach(stage => {
                const stageName = stage.name ? stage.name.toLowerCase() : '';
                // Исключаем стадии с "готово"
                if (!stageName.includes('готово')) {
                  totalCount += stage.count || 0;
                }
              });
            }
            
            // Также добавляем из production стадий "Новые заказы" и "Закуп"
            if (dashboardData.stages) {
              dashboardData.stages.forEach(stage => {
                const stageName = stage.name ? stage.name.toLowerCase() : '';
                if (stageName.includes('новые заказы') || 
                    stageName.includes('закуп') ||
                    (stageName.includes('новые') && stageName.includes('заказы'))) {
                  totalCount += stage.count || 0;
                }
              });
            }
            
            return totalCount;
          })()}
          subtitle="активные"
          color="#F59E0B"
        />
        <CompactSummaryCard
          title="Потенциальные Заказы"
          value={potentialOrdersCount}
          color="#EAB308"
        />
        
        <CompactSummaryCard
          title="Дата и время"
          value={currentTime.toLocaleString('ru-RU', { 
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit', 
            minute: '2-digit',
            second: '2-digit'
          })}
          color="#EF4444"
        />
        <CompactSummaryCard
          title="Алматы"
          value={weather ? `${Math.round(weather.main.temp)}°C` : '?'}
          subtitle={weather ? weather.weather[0].description : 'Загрузка...'}
          color="#06B6D4"
        />
        <CompactSummaryCard
          title="Поступило Сегодня"
          value={dashboardData.summary?.todayNewOrders || 0}
          
          color={
            (dashboardData.summary?.urgentOrders || 0) > 0 
              ? "#EF4444" // Красный, если есть срочные заказы
              : "#10B981"  // Зеленый, если срочных нет
          }
        />
      </div>

      {/* Основной контент */}
      <div className="flex gap-1" style={{ height: '698px' }}>
        <div style={{ flex: '3' }}>
          {ProductionSection}
        </div>
        <div style={{ flex: '1' }} className="flex flex-col gap-1">
          <div style={{ height: '55%' }}>
            {WarehouseSection}
          </div>
        </div>
      </div>


      
    </div>
  );
};

export default TVDashboardPage;
